import { WebhookEntityType } from '../entities/webhooks-v2/webhook.entity.type';
import { WebhookSubscriptionFilters } from '../entities/webhooks-v2/webhook.subscription';

/** Field holding the customer on each entity's record; an entity not listed keeps it on `customer`. Guessing would match a quote's contact person. */
const CUSTOMER_FIELD: Partial<Record<string, string>> = {
    [WebhookEntityType.Invoice]: 'contact',
    [WebhookEntityType.Bill]: 'contact',
    [WebhookEntityType.Quote]: 'account',
    [WebhookEntityType.Opportunity]: 'account',
    [WebhookEntityType.Lead]: 'account',
};

/** Decides whether one entity record passes a subscription's filters; used by webhook dispatch and agent triggers. */
export class EntitySubscriptionFilterMatcher {

    /** `entityType` is the subscription's entity (webhook entity or agent source type) and picks where each field lives on the record. */
    public static matches(entityType: string, entityData: Record<string, unknown> | null | undefined, filters: WebhookSubscriptionFilters | undefined): boolean {

        if (!filters || Object.keys(filters).length === 0) {
            return true;
        }

        if (!entityData) {
            return false;
        }

        const data = this.getFilterSource(entityData);

        if (filters.customer?.length) {
            const customerId = this.extractId(data, CUSTOMER_FIELD[entityType] ?? 'customer');
            if (!customerId || !filters.customer.includes(customerId)) {
                return false;
            }
        }

        if (filters.site?.length) {
            const siteId = this.extractId(data, 'site');
            if (!siteId || !filters.site.includes(siteId)) {
                return false;
            }
        }

        if (filters.type?.length) {
            const typeId = this.extractId(data, 'type') || this.extractId(data, 'jobType');
            if (!typeId || !filters.type.includes(typeId)) {
                return false;
            }
        }

        if (filters.priority?.length) {
            const priorityId = this.extractId(data, 'priority');
            if (!priorityId || !filters.priority.includes(priorityId)) {
                return false;
            }
        }

        if (filters.owner?.length) {
            const ownerId = this.extractId(data, 'owner');
            if (!ownerId || !filters.owner.includes(ownerId)) {
                return false;
            }
        }

        if (filters.resource?.length) {
            const resourceId = this.extractId(data, 'resource') || this.extractId(data, 'technician');
            if (!resourceId || !filters.resource.includes(resourceId)) {
                return false;
            }
        }

        if (filters.billingCompany?.length) {
            const companyId = this.extractId(data, 'billingCompany') || this.extractId(data, 'company');
            if (!companyId || !filters.billingCompany.includes(companyId)) {
                return false;
            }
        }

        if (filters.tag?.length) {
            const tags = (data.tags as Array<{ _id: string }>) || [];
            if (!tags.some(tag => filters.tag!.includes(tag._id))) {
                return false;
            }
        }

        if (filters.status?.length) {
            const status = data.status as Record<string, unknown> | string;
            if (!this.matchesStatusFilter(status, filters.status)) {
                return false;
            }
        }

        if (filters.stageStatus?.length) {
            const status = data.status as Record<string, unknown> | string;
            if (!this.matchesStatusFilter(status, filters.stageStatus)) {
                return false;
            }
        }

        if (filters.zone?.length) {
            const zoneId = this.extractId(data, 'zone');
            if (!zoneId || !filters.zone.includes(zoneId)) {
                return false;
            }
        }

        if (filters.opportunityStage?.length) {
            const stageId = this.extractId(data, 'stage');
            if (!stageId || !filters.opportunityStage.includes(stageId)) {
                return false;
            }
        }

        if (filters.leadStage?.length) {
            const stage = this.extractString(data, 'stage');
            if (!stage || !filters.leadStage.includes(stage)) {
                return false;
            }
        }

        if (filters.leadSource?.length) {
            const sourceId = this.extractId(data, 'source');
            if (!sourceId || !filters.leadSource.includes(sourceId)) {
                return false;
            }
        }

        if (filters.activityEntity?.length) {
            const activityEntity = this.extractString(data, 'entity');
            if (!activityEntity || !filters.activityEntity.includes(activityEntity)) {
                return false;
            }
        }

        const activityKindFilters = filters.activityKind?.length ? filters.activityKind : filters.activityType;
        if (activityKindFilters?.length) {
            const activityKindOrType = this.extractString(data, 'kind') || this.extractString(data, 'type');
            if (!activityKindOrType || !activityKindFilters.includes(activityKindOrType)) {
                return false;
            }
        }

        return true;
    }

    private static getFilterSource(data: Record<string, unknown>): Record<string, unknown> {

        const after = data.after;
        if (after && typeof after === 'object') {
            return after as Record<string, unknown>;
        }

        const value = data.value;
        if (value && typeof value === 'object') {
            return value as Record<string, unknown>;
        }

        const before = data.before;
        if (before && typeof before === 'object') {
            return before as Record<string, unknown>;
        }

        return data;
    }

    private static extractId(data: Record<string, unknown>, field: string): string | null {

        const value = data[field];
        if (!value) {
            return null;
        }
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'object' && '_id' in value) {
            return (value as Record<string, unknown>)._id as string;
        }
        return null;
    }

    private static extractString(data: Record<string, unknown>, field: string): string | null {

        const value = data[field];
        return typeof value === 'string' ? value : null;
    }

    private static matchesStatusFilter(status: Record<string, unknown> | string | undefined, filterItems: Array<{ match: string; value: string }>): boolean {

        if (!status) {
            return false;
        }

        for (const filter of filterItems) {
            if (typeof status === 'string') {
                if (filter.match === 'systemStatus' && status === filter.value) {
                    return true;
                }
                if (filter.match === 'id' && status === filter.value) {
                    return true;
                }
                continue;
            }

            if (filter.match === 'id' && status._id === filter.value) {
                return true;
            }
            if (filter.match === 'systemStatus' && status.systemStatus === filter.value) {
                return true;
            }
            if (filter.match === 'systemName' && status.systemName === filter.value) {
                return true;
            }
        }

        return false;
    }
}
